import { useId, useState } from 'react';
import {
  getRxFileDownloadUrl,
  getRxFilePreviewUrl,
  TimelineEventAttachmentType,
  TimelineEventAttachmentStatus,
  type TimelineEvent,
} from '../../api';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import styled from 'styled-components';
import { formatSimpleDate } from 'shared-utils/utils/formatDate';
import { BulletPoint } from '../TimelineItem/TimelineItem';
import { createTrackingProps } from 'shared-utils/analytics';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import FilePreviewModal from 'design-system/src/components/FilePreviewModal/FilePreviewModal';
import { SubmittedPAModal } from '../SubmittedPAModal';
import { useParams } from 'react-router-dom';

const Flex = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  position: relative;
`;

const SubwayTrack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 24px;
  align-items: center;
  // lines up the bullet with the first line of event text
  padding-top: 21px;
`;

const Rail = styled.div<{ $hasBorder: boolean; $color: string; $position: 'top' | 'bottom' }>`
  background-color: ${({ $hasBorder, $color }) => ($hasBorder ? $color : 'transparent')};
  width: 1px;
  position: absolute;
  left: 12px; /* 24px/2 for SubwayTrack center */

  // this styling is odd but it allows the rail to always be positioned correctly regardless of the event text height
  ${({ $position }) =>
    $position === 'top'
      ? `
    top: 0;
    height: 17px;
  `
      : `
    top: 32px;
    bottom: 0;
  `}
`;

const StyledBulletPoint = styled(BulletPoint)`
  // Using && to increase CSS specificity when extending a styled component.
  // This ensures these style overrides take precedence over the base BulletPoint styles
  // without needing !important, which is more maintainable.
  && {
    width: auto;
    height: auto;
    min-width: 6px;
    min-height: 6px;
    // 1px left margin to align with the rail
    margin: 0 0 0 1px;
  }
`;

const SpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
`;

const RowContent = styled(SpaceBetween)<{ $hasBorder: boolean }>`
  border-bottom: ${({ $hasBorder }) => ($hasBorder ? '1px solid var(--grayscale-100)' : 'none')};
`;

const EventText = styled(Typography)<{ $hasSubtitle: boolean }>`
  // Center the text vertically within the 48px container, where the event text height is 18px and the subtitle height is 16.7px
  // Single line: 15px top and bottom padding
  // With subtitle: 8px top + 2px bottom (for spacing between text and subtitle)
  padding: ${({ $hasSubtitle }) => ($hasSubtitle ? '8px 0 2px 0' : '15px 0')};
  // for handling newlines in the event text
  white-space: pre-wrap;
`;

const EventSubtitle = styled(Typography)`
  // for handling newlines in the event text
  white-space: pre-wrap;
  // Add bottom padding to center the two-line text block
  padding-bottom: 8px;
`;

const EventTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const AttachmentLinkAndTimestamp = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const WhiteButton = styled(Button).attrs({
  variant: 'tertiary',
  size: 'xs',
})`
  background-color: var(--white);
`;

const MobileButton = styled(WhiteButton)`
  // intentionally using a breakpoint 1px below what DesktopButton is using
  // to avoid a gap when neither button is visible
  @media (min-width: ${breakpoints.smallDesktopMin}px) {
    display: none;
  }
`;

const DesktopButton = styled(WhiteButton)`
  // intentionally using a breakpoint 1px above what MobileButton is using
  // to avoid a gap when neither button is visible
  @media (max-width: ${breakpoints.tabletMax}px) {
    display: none;
  }
`;

const Timestamp = styled(Typography)`
  min-width: 56px;
`;

export type TimelineEventItemProps = {
  /**
   * Class name for additional styling
   */
  className?: string;
  /**
   * The event data to display
   */
  event: TimelineEvent;
  /**
   * Whether this is the last event in the timeline
   */
  isLastEvent: boolean;
  /**
   * Color for the rail and bullet point
   */
  subwayTrackColor: string;
};

/**
 * Individual timeline event item with dynamic subway track height calculation
 */
export const TimelineEventItem = ({ className, event, isLastEvent, subwayTrackColor }: TimelineEventItemProps) => {
  const [isFilePreviewVisible, setIsFilePreviewVisible] = useState(false);
  const params = useParams();
  const id = useId();

  const getSubtitleText = (event: TimelineEvent): string | null => {
    if (event.subtitle) {
      return event.subtitle;
    }

    if (!('attachment_type' in event)) return null;

    const { attachment_type, attachment_data } = event;

    // For tasks/reviews, show requested date
    if (
      (attachment_type === TimelineEventAttachmentType.TASK ||
        attachment_type === TimelineEventAttachmentType.PA_REVIEW) &&
      attachment_data.created_at
    ) {
      return `Requested on ${formatSimpleDate(attachment_data.created_at)}`;
    }

    // For documents, show the document name
    if (attachment_type === TimelineEventAttachmentType.DOCUMENT && attachment_data.document_type) {
      return attachment_data.document_type;
    }

    // For other cases, no subtitle
    return null;
  };

  const subtitleText = getSubtitleText(event);

  const getAttachmentLink = () => {
    if (!('attachment_type' in event)) return null;

    switch (event.attachment_type) {
      case TimelineEventAttachmentType.DOCUMENT:
        return (
          <>
            <MobileButton href={getRxFileDownloadUrl(params.rxId || '', event.attachment_data.document_id)}>
              Download document
            </MobileButton>
            <DesktopButton onClick={() => setIsFilePreviewVisible(true)}>Preview document</DesktopButton>
            {isFilePreviewVisible && (
              <FilePreviewModal
                fileUrl={getRxFilePreviewUrl(params.rxId || '', event.attachment_data.document_id)}
                onClose={() => setIsFilePreviewVisible(false)}
                fileHeader={event.attachment_data.document_type}
                downloadUrl={getRxFileDownloadUrl(params.rxId || '', event.attachment_data.document_id)}
              />
            )}
          </>
        );
      case TimelineEventAttachmentType.TASK:
        if (event.attachment_data.status === TimelineEventAttachmentStatus.OPEN) {
          return (
            <Button variant="primary" size="xs" to={`/tasks/${event.attachment_data.task_id}`}>
              Complete Task
            </Button>
          );
        } else {
          return <WhiteButton to={`/tasks/${event.attachment_data.task_id}`}>View Task</WhiteButton>;
        }
      case TimelineEventAttachmentType.PA_REVIEW:
        if (event.attachment_data.status === TimelineEventAttachmentStatus.OPEN) {
          return (
            <Button variant="primary" size="xs" to={`/prior-auth-reviews/${event.attachment_data.review_id}`}>
              Complete Review
            </Button>
          );
        } else {
          return (
            <WhiteButton to={`/prior-auth-reviews/${event.attachment_data.review_id}`}>View PA Review</WhiteButton>
          );
        }
      case TimelineEventAttachmentType.PA_SUBMISSION:
        if (!event.attachment_data.form_tree_state_id && !event.attachment_data.submission_merged_document_id)
          return null;
        return (
          <>
            <WhiteButton onClick={() => setIsFilePreviewVisible(true)}>View submitted PA</WhiteButton>
            {isFilePreviewVisible && (
              <SubmittedPAModal
                rxId={params.rxId || ''}
                formTreeStateId={event.attachment_data.form_tree_state_id}
                submissionMergedDocumentId={event.attachment_data.submission_merged_document_id}
                onClose={() => setIsFilePreviewVisible(false)}
              />
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Flex id={id} className={className} {...createTrackingProps({ unmask: true })}>
      <SubwayTrack>
        <Rail $hasBorder $color={subwayTrackColor} $position="top" />
        <StyledBulletPoint $color={subwayTrackColor} />
        <Rail $hasBorder={!isLastEvent} $color={subwayTrackColor} $position="bottom" />
      </SubwayTrack>

      <RowContent $hasBorder={!isLastEvent}>
        <EventTextContainer>
          <EventText
            styledAs="bodySmallDMSans"
            weight={event.is_status_change ? 600 : 400}
            $hasSubtitle={!!subtitleText}
          >
            {event.event}
          </EventText>
          {!!subtitleText && (
            <EventSubtitle styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
              {subtitleText}
            </EventSubtitle>
          )}
        </EventTextContainer>

        <AttachmentLinkAndTimestamp>
          {getAttachmentLink()}

          {event.timestamp && (
            <Timestamp styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
              {formatSimpleDate(event.timestamp)}
            </Timestamp>
          )}
        </AttachmentLinkAndTimestamp>
      </RowContent>
    </Flex>
  );
};

export default TimelineEventItem;
